import * as React from 'react';
import { motion, AnimatePresence, MotionConfig } from 'framer-motion';
import { ChevronDown } from 'lucide-react';

interface DropdownOption {
  id: string;
  value: string;
}

interface FluidDropdownProps {
  selectedOption: DropdownOption | undefined;
  options: DropdownOption[] | undefined;
  placeholder: string;
  onSelect: (option: DropdownOption) => void;
}

function useClickAway(
  ref: React.RefObject<HTMLElement>,
  handler: () => void,
) {
  React.useEffect(() => {
    const listener = (event: MouseEvent | TouchEvent) => {
      if (!ref.current || ref.current.contains(event.target as Node)) return;
      handler();
    };
    document.addEventListener('mousedown', listener);
    document.addEventListener('touchstart', listener);
    return () => {
      document.removeEventListener('mousedown', listener);
      document.removeEventListener('touchstart', listener);
    };
  }, [ref, handler]);
}

const containerVariants: any = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { when: 'beforeChildren', staggerChildren: 0.02 },
  },
};

const itemVariants: any = {
  hidden: { opacity: 0, y: -4 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.1 },
  },
};

export function FluidDropdown({ selectedOption, options, placeholder, onSelect }: FluidDropdownProps) {
  const [isOpen, setIsOpen] = React.useState(false);
  const [hoveredId, setHoveredId] = React.useState<string | null>(null);
  const dropdownRef = React.useRef<HTMLDivElement>(null) as React.RefObject<HTMLDivElement>;

  useClickAway(dropdownRef, () => setIsOpen(false));

  const handleSelect = (option: DropdownOption) => {
    onSelect(option);
    setIsOpen(false);
  };

  return (
    <MotionConfig reducedMotion="user">
      <div className="w-full relative" ref={dropdownRef}>
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="w-full flex items-center justify-between h-10 px-3 rounded-lg text-sm font-medium transition-all duration-200 border border-zinc-700 bg-zinc-900 text-zinc-300 hover:bg-zinc-800 hover:text-white focus:outline-none focus:ring-2 focus:ring-orange-500/50 cursor-pointer"
          style={isOpen ? { backgroundColor: '#27272a', color: 'white' } : {}}
        >
          <span className="truncate">
            {selectedOption ? selectedOption.value : placeholder}
          </span>
          <motion.div
            animate={{ rotate: isOpen ? 180 : 0 }}
            transition={{ duration: 0.2 }}
            className="flex items-center justify-center w-5 h-5 ml-2 flex-shrink-0"
          >
            <ChevronDown className="w-4 h-4" />
          </motion.div>
        </button>

        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{
                opacity: 1,
                height: 'auto',
                transition: { type: 'spring', stiffness: 800, damping: 35, mass: 0.5 },
              }}
              exit={{
                opacity: 0,
                height: 0,
                transition: { duration: 0.12 },
              }}
              className="absolute left-0 right-0 top-full mt-1.5 z-50"
            >
              <motion.div
                className="w-full rounded-lg border border-zinc-800 bg-zinc-900 p-1 shadow-xl max-h-[240px] overflow-y-auto"
                initial={{ borderRadius: 8 }}
                animate={{ borderRadius: 12, transition: { duration: 0.2 } }}
                style={{ transformOrigin: 'top', scrollbarWidth: 'thin', scrollbarColor: '#3f3f46 transparent' }}
              >
                <motion.div
                  className="py-1 relative"
                  variants={containerVariants}
                  initial="hidden"
                  animate="visible"
                >
                  {options && options.length > 0 ? (
                    options.map((option) => (
                      <motion.button
                        key={option.id}
                        type="button"
                        onClick={() => handleSelect(option)}
                        onMouseEnter={() => setHoveredId(option.id)}
                        onMouseLeave={() => setHoveredId(null)}
                        className="relative flex w-full items-center px-3 py-2 text-sm rounded-md transition-colors duration-150 focus:outline-none border-none cursor-pointer bg-transparent"
                        style={{
                          color:
                            selectedOption?.id === option.id || hoveredId === option.id
                              ? 'white'
                              : '#a1a1aa',
                          backgroundColor:
                            hoveredId === option.id
                              ? '#27272a'
                              : selectedOption?.id === option.id
                                ? '#1c1c1e'
                                : 'transparent',
                        }}
                        whileTap={{ scale: 0.98 }}
                        variants={itemVariants}
                      >
                        {option.value}
                      </motion.button>
                    ))
                  ) : (
                    <div className="px-3 py-2 text-sm text-zinc-600">Nenhuma opção</div>
                  )}
                </motion.div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </MotionConfig>
  );
}
